import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, X, CheckCheck, Clock, Mail, Smartphone, AlertCircle } from 'lucide-react'
import { Notification, NotificationType } from '../types'
import { apiService } from '../services/api'
import { useSocket } from '../contexts/SocketContext'
import { useToast } from '../contexts/ToastContext'
import { Socket } from 'socket.io-client'

interface NotificationCenterProps {
  className?: string
}

export default function NotificationCenter({ className = '' }: NotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const lastFetchTime = useRef<number>(0)
  const { socket } = useSocket()
  const navigate = useNavigate()
  const { showToast } = useToast()

  // Fetch notifications and unread count
  const fetchNotifications = async () => {
    try {
      setLoading(true)
      const [notificationsResponse, unreadResponse] = await Promise.all([
        apiService.get('/notifications'),
        apiService.get('/notifications/unread-count')
      ])

      if (notificationsResponse.success) {
        setNotifications(notificationsResponse.data.notifications)
      }

      if (unreadResponse.success) {
        setUnreadCount(unreadResponse.data.unreadCount)
      }
      
      // Update last fetch time
      lastFetchTime.current = Date.now()
    } catch (error) {
      // Failed to fetch notifications - silently continue
    } finally {
      setLoading(false)
    }
  }

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      const response = await apiService.put(`/notifications/${notificationId}/read`)
      if (response.success) {
        setNotifications(prev => 
          prev.map(n => 
            n.id === notificationId 
              ? { ...n, status: 'read', readAt: new Date().toISOString() }
              : n
          )
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (error) {
      // Failed to mark notification as read - silently continue
    }
  }

  // Handle notification click with navigation
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread
    if (!notification.readAt) {
      await markAsRead(notification.id)
    }

    // Navigate based on notification type and data
    if (notification.type === 'auction_won' && notification.data?.itemUrl) {
      setIsOpen(false) // Close dropdown
      navigate(notification.data.itemUrl)
    } else if (notification.data?.auctionId && notification.data?.itemId) {
      // For bid-related notifications, navigate to live site item details page
      try {
        const auctionResponse = await apiService.get(`/auctions/${notification.data.auctionId}/basic`)
        if (auctionResponse.success && auctionResponse.data?.url) {
          setIsOpen(false) // Close dropdown
          navigate(`/${auctionResponse.data.url}/${notification.data.itemId}`)
        } else {
          // Auction not found (likely deleted), show popup and navigate to auctions list
          setIsOpen(false) // Close dropdown
          showToast('This auction has been deleted and is no longer available. You will be redirected to your auctions list.', 'warning', 6000)
          navigate('/auctions')
        }
      } catch (error) {
        // Auction likely deleted, show popup and navigate to auctions list as fallback
        setIsOpen(false) // Close dropdown
        showToast('This auction has been deleted and is no longer available. You will be redirected to your auctions list.', 'warning', 6000)
        navigate('/auctions')
      }
    } else if (notification.data?.auctionId) {
      // For auction-only notifications, get the auction URL
      try {
        const auctionResponse = await apiService.get(`/auctions/${notification.data.auctionId}/basic`)
        if (auctionResponse.success && auctionResponse.data?.url) {
          setIsOpen(false) // Close dropdown
          navigate(`/${auctionResponse.data.url}`)
        } else {
          // Auction not found (likely deleted), show popup and navigate to auctions list
          setIsOpen(false) // Close dropdown
          showToast('This auction has been deleted and is no longer available. You will be redirected to your auctions list.', 'warning', 6000)
          navigate('/auctions')
        }
      } catch (error) {
        // Auction likely deleted, show popup and navigate to auctions list as fallback
        setIsOpen(false) // Close dropdown
        showToast('This auction has been deleted and is no longer available. You will be redirected to your auctions list.', 'warning', 6000)
        navigate('/auctions')
      }
    }
  }

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const response = await apiService.put('/notifications/mark-all-read')
      if (response.success) {
        setNotifications(prev => 
          prev.map(n => ({ ...n, status: 'read' as const, readAt: new Date().toISOString() }))
        )
        setUnreadCount(0)
      }
    } catch (error) {
      // Failed to mark all notifications as read - silently continue
    }
  }

  // Get notification icon based on type
  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'bid_placed': return '💰'
      case 'outbid': return '⚡'
      case 'outbid_live_auction': return '🔴'
      case 'auction_ending': return '⏰'
      case 'auction_won': return '🎉'
      case 'payment_required': return '💳'
      case 'auction_started': return '🚀'
      case 'auction_cancelled': return '❌'
      default: return '🔔'
    }
  }

  // Get notification color based on type
  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case 'bid_placed': return 'text-green-600'
      case 'outbid': return 'text-orange-600'
      case 'outbid_live_auction': return 'text-red-600'
      case 'auction_ending': return 'text-red-600'
      case 'auction_won': return 'text-purple-600'
      case 'payment_required': return 'text-blue-600'
      case 'auction_started': return 'text-indigo-600'
      case 'auction_cancelled': return 'text-gray-600'
      default: return 'text-gray-600'
    }
  }

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
    return date.toLocaleDateString()
  }

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch notifications on mount and cache them
  useEffect(() => {
    fetchNotifications()
  }, [])

  // Only fetch unread count when opened (notifications are already cached)
  useEffect(() => {
    if (isOpen) {
      // Only refresh if notifications are stale (older than 30 seconds)
      const now = Date.now()
      const shouldRefresh = notifications.length === 0 || (now - lastFetchTime.current > 30000)
      
      if (shouldRefresh) {
        fetchNotifications()
      }
    }
  }, [isOpen])

  // Listen for real-time notifications via socket
  useEffect(() => {
    if (socket && socket.connected) {
      const socketInstance = socket as Socket
      
      const handleNotification = (notification: any) => {
        // Add the new notification to the list
        setNotifications(prev => [notification, ...prev])
        setUnreadCount(prev => prev + 1)
      }

      // Listen for notifications
      socketInstance.on('notification', handleNotification)

      return () => {
        socketInstance.off('notification', handleNotification)
      }
    }

    // Fallback: poll for updates every 30 seconds if no socket or not connected
    const interval = setInterval(() => {
      if (!isOpen) {
        fetchNotifications()
      }
    }, 30000)

    return () => clearInterval(interval)
  }, [socket, isOpen])


  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-md border-2 border-gray-300 hover:border-gray-400 dark:border-dark-secondary dark:hover:border-dark-secondary bg-white dark:bg-dark-tertiary text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-all duration-200 hover:shadow-md min-h-[44px] min-w-[44px] touch-manipulation"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5 mx-auto" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-gray-500">
                <Bell className="h-12 w-12 mb-2 opacity-50" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notification.readAt ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Notification Icon */}
                      <div className={`flex-shrink-0 text-2xl ${getNotificationColor(notification.type)}`}>
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Notification Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${!notification.readAt ? 'text-gray-900' : 'text-gray-700'}`}>
                            {notification.title}
                          </p>
                          <div className="flex items-center space-x-1">
                            {/* Channel indicators */}
                            {notification.channels.includes('email') && (
                              <Mail className="h-3 w-3 text-gray-400" />
                            )}
                            {notification.channels.includes('socket') && (
                              <Smartphone className="h-3 w-3 text-gray-400" />
                            )}
                            {/* Status indicator */}
                            {notification.status === 'failed' && (
                              <AlertCircle className="h-3 w-3 text-red-500" />
                            )}
                            {notification.readAt ? (
                              <CheckCheck className="h-3 w-3 text-green-500" />
                            ) : (
                              <Clock className="h-3 w-3 text-blue-500" />
                            )}
                          </div>
                        </div>
                        <p className={`text-sm mt-1 ${!notification.readAt ? 'text-gray-800' : 'text-gray-600'}`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatRelativeTime(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-gray-200 p-3">
              <button
                onClick={() => {
                  setIsOpen(false)
                  // Navigate to full notifications page if you have one
                  // navigate('/notifications')
                }}
                className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}